import { useCallback, useEffect } from 'react';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { CreateSubscriptionActions, OnClickActions } from '@paypal/paypal-js';
import { env } from 'src/env';
import { AppRoutes, GTMEvent, SubscriptionPlanInfo } from 'src/types';
import { useBanner, useSdkLoader, useSession } from 'src/hooks';
import { setWithExpiry } from 'src/utils/localStorage';
import {
  PAYPAL_PAYMENT_IN_PROGRESS,
  PAYPAL_PAYMENT_IN_PROGRESS_BANNER_TTL_SECONDS,
  PAYMENT_ERROR,
  PAYMENT_ERROR_MESSAGE,
  REFERRAL_ID,
} from 'src/constants';
import { useNavigate } from 'react-router-dom';
import { isSuperNinjaPlan, sendGTMEvent, sendPaymentGTMEvent } from 'src/utils';
import { toast } from 'react-toastify';
import useSafeLogger from 'src/hooks/useSafeLogger';
import { OnApproveActions } from '@paypal/paypal-js/types/components/buttons';
import { useCreatePaypalSubscriptionMutation } from 'src/store/services';
import { useChoosePlanModal } from 'src/hooks/useChoosePlanModal';
import { PaymentProgressBar } from '../../componentsV2/PaymentProgressBar';
import styles from './PayPalElements.module.scss';
import { useFeatureFlagEnabled } from 'posthog-js/react';

const PAYMENT_PROVIDER = 'paypal';

interface PayPalElementsProps {
  selectedPlan?: SubscriptionPlanInfo;
  onPaymentProcessing: (value: boolean) => void;
  numberOfSeats?: number;
  isUpdatePayment?: boolean;
  onSdkReady?: () => void;
}

export const PayPalElements = ({
  selectedPlan,
  onPaymentProcessing,
  numberOfSeats,
  isUpdatePayment,
  onSdkReady,
}: PayPalElementsProps) => {
  const navigate = useNavigate();
  const { pgLogError } = useSafeLogger();
  const {
    shouldShowPlanModal,
    changeShouldShowPlanModal,
    changeNeverShowChoosePlanModalAgain,
  } = useChoosePlanModal();
  const canRedirectToSN = true;

  const enableHandlingOfBusinessLogicInSN =
    useFeatureFlagEnabled('handleBusinessInSN');

  const {
    appUser: { user_id },
  } = useSession();

  const [createPaypalSubscriptionMutation] =
    useCreatePaypalSubscriptionMutation();

  const { removeAllPaymentBanners } = useBanner();

  const {
    timedOut: isPaypalSdkTimedOut,
    loading: isPaypalSdkLoading,
    start: startPaypalSdk,
    resolve: resolvePaypalSdk,
    reset: resetPaypalSdk,
  } = useSdkLoader();

  useEffect(() => {
    startPaypalSdk();

    return () => {
      resetPaypalSdk();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isPaypalSdkTimedOut) {
      pgLogError('paypal sdk timed out', {
        user_id,
        payment_provider: PAYMENT_PROVIDER,
        plan: selectedPlan?.plan_id,
        step: 'paypal sdk timed out',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaypalSdkTimedOut]);

  const handleInit = useCallback(() => {
    resolvePaypalSdk();
    onSdkReady?.();
  }, [resolvePaypalSdk, onSdkReady]);

  const createPaypalSubscription = useCallback(
    async (
      data: Record<string, unknown>,
      actions: CreateSubscriptionActions,
    ): Promise<string> => {
      if (selectedPlan?.metadata?.paypal) {
        return actions.subscription.create({
          plan_id: selectedPlan?.metadata?.paypal.plan_id,
          quantity: numberOfSeats ? numberOfSeats.toString() : undefined,
          custom_id: user_id,
        });
      }
      throw new Error(
        "Missing paypal details in the subscription plan's metadata",
      );
    },
    [selectedPlan?.metadata?.paypal, user_id, numberOfSeats],
  );

  const onApprovePaypalSubscription = useCallback(
    async (
      data: Record<string, unknown>,
      actions: OnApproveActions,
    ): Promise<void> => {
      onPaymentProcessing(true);
      try {
        await createPaypalSubscriptionMutation({
          user_id,
          subscription_id: data.subscriptionID as string,
        }).unwrap();
        localStorage.removeItem(REFERRAL_ID);
      } catch (e) {
        // No need to notify user, this event will be handled by PayPal webhook
        setWithExpiry(
          PAYPAL_PAYMENT_IN_PROGRESS,
          'true',
          PAYPAL_PAYMENT_IN_PROGRESS_BANNER_TTL_SECONDS,
        );
      }
      const dataGTM = {
        transaction_id: data.subscriptionID as string,
        subscription_plan: selectedPlan?.plan_tier,
        subscription_period: selectedPlan?.period,
        number_of_seats: numberOfSeats ? numberOfSeats.toString() : undefined,
        value: Math.round((selectedPlan?.original_price || 0) / 100),
        currency: selectedPlan?.currency || 'USD',
      };
      sendPaymentGTMEvent(dataGTM);

      if (!isUpdatePayment) {
        onPaymentProcessing(false);

        if (shouldShowPlanModal) {
          changeShouldShowPlanModal(false);
        }

        changeNeverShowChoosePlanModalAgain(true);

        removeAllPaymentBanners();
        const isSNPlan = isSuperNinjaPlan(selectedPlan?.plan_family);

        if (
          (isSNPlan && canRedirectToSN) ||
          enableHandlingOfBusinessLogicInSN
        ) {
          window.location.href = env.REACT_APP_SUPER_NINJA_URL;
        } else {
          navigate(AppRoutes.HOME, { replace: true });
        }
      }
    },
    [
      onPaymentProcessing,
      selectedPlan?.plan_tier,
      selectedPlan?.period,
      selectedPlan?.original_price,
      selectedPlan?.currency,
      selectedPlan?.plan_family,
      navigate,
      removeAllPaymentBanners,
      createPaypalSubscriptionMutation,
      user_id,
      numberOfSeats,
      isUpdatePayment,
      changeShouldShowPlanModal,
      shouldShowPlanModal,
      changeNeverShowChoosePlanModalAgain,
      canRedirectToSN,
      enableHandlingOfBusinessLogicInSN,
    ],
  );

  const onErrorPaypalSubscription = useCallback(
    (error: Record<string, unknown>) => {
      localStorage.removeItem(PAYPAL_PAYMENT_IN_PROGRESS);

      toast.error(PAYMENT_ERROR_MESSAGE);

      pgLogError(PAYMENT_ERROR, {
        user_id,
        payment_provider: PAYMENT_PROVIDER,
        plan: selectedPlan?.plan_id,
        error: JSON.stringify(error),
      });
    },
    [selectedPlan?.plan_id, user_id, pgLogError],
  );

  const handlePaypalClick = (
    data: Record<string, unknown>,
    actions: OnClickActions,
  ) => {
    sendGTMEvent(GTMEvent.PAYMENT_METHOD_CLICK, {
      payment_method_type: data.fundingSource as string,
    });
  };

  if (isPaypalSdkTimedOut) {
    return (
      <div className={styles.errorContainer}>
        <div>
          We couldn’t connect to PayPal at the moment. Please try again later or
          use another payment method.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <PayPalScriptProvider
        options={{
          clientId: env.REACT_APP_PAYPAL_CLIENT_ID,
          vault: true,
          intent: 'subscription',
        }}
      >
        {isPaypalSdkLoading && (
          <PaymentProgressBar
            title="Loading PayPal…"
            className={styles.progressBar}
          />
        )}

        <PayPalButtons
          createSubscription={createPaypalSubscription}
          onApprove={onApprovePaypalSubscription}
          onError={onErrorPaypalSubscription}
          onInit={handleInit}
          fundingSource={'paypal'}
          onClick={handlePaypalClick}
          style={{
            layout: 'vertical',
            color: 'gold',
            label: 'paypal',
            borderRadius: 12,
            height: 53,
          }}
          forceReRender={[selectedPlan, numberOfSeats]}
        />
      </PayPalScriptProvider>
    </div>
  );
};
